import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import Logo from "./Logo";

const LINKS = [
  ["nav.home", "/"],
  ["nav.about", "/about"],
  ["nav.services", "/services"],
  ["nav.training", "/training"],
  ["nav.experts", "/experts"],
  ["nav.insights", "/insights"],
  ["nav.videos", "/videos"],
  ["nav.contact", "/contact"],
];

export default function Navbar() {
  const { t, lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        scrolled ? "border-slate-200/80 bg-white/90 backdrop-blur-xl shadow-sm" : "border-transparent bg-white"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-3 sm:px-6 lg:px-4 xl:px-12">
        <Link to="/" aria-label="Stratedge UK home">
          <span className="xl:hidden"><Logo compact /></span>
          <span className="hidden xl:inline-flex"><Logo /></span>
        </Link>

        <nav className="hidden items-center gap-3 lg:flex xl:gap-5" aria-label="Primary">
          {LINKS.map(([key, to]) => (
            <NavLink
              key={key}
              to={to}
              data-testid={`nav-link-${key.split(".")[1]}`}
              className={({ isActive }) =>
                `relative whitespace-nowrap text-[11px] font-semibold tracking-wide transition-colors hover:text-gold-hover xl:text-[13px] ${
                  isActive ? "text-navy after:absolute after:-bottom-2 after:start-0 after:h-[2px] after:w-full after:bg-gold" : "text-slate-600"
                }`
              }
            >
              {t(key)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex xl:gap-3">
          <button
            data-testid="language-switcher-button"
            onClick={() => setLang(lang === "en" ? "ar" : "en")}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-slate-200 px-2.5 py-1.5 text-[10.5px] font-semibold text-slate-700 transition-colors hover:border-gold hover:text-gold-hover xl:px-3 xl:text-xs"
          >
            <Globe className="h-3.5 w-3.5" />
            {lang === "en" ? "العربية" : "EN"}
          </button>
          <Link
            to="/join"
            data-testid="nav-join-network-cta"
            className="whitespace-nowrap rounded-full border border-navy px-3 py-1.5 text-[10.5px] font-semibold text-navy transition-colors hover:bg-navy hover:text-white xl:px-4 xl:py-2 xl:text-xs"
          >
            {t("nav.join")}
          </Link>
          <Link
            to="/consultation"
            data-testid="nav-book-consultation-cta"
            className="whitespace-nowrap rounded-full bg-gold px-3 py-1.5 text-[10.5px] font-bold text-navy transition-colors hover:bg-gold-hover xl:px-4 xl:py-2 xl:text-xs"
          >
            {t("nav.book")}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="rounded-md p-2 text-navy lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="border-t border-slate-100 bg-white px-4 pb-6 pt-2 lg:hidden">
          {LINKS.map(([key, to]) => (
            <NavLink
              key={key}
              to={to}
              className={({ isActive }) =>
                `block border-b border-slate-50 py-3 text-sm font-semibold ${isActive ? "text-gold-hover" : "text-slate-700"}`
              }
            >
              {t(key)}
            </NavLink>
          ))}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              data-testid="language-switcher-mobile"
              onClick={() => setLang(lang === "en" ? "ar" : "en")}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700"
            >
              <Globe className="h-3.5 w-3.5" />
              {lang === "en" ? "العربية" : "English"}
            </button>
            <Link to="/join" className="rounded-full border border-navy px-4 py-2 text-xs font-semibold text-navy">
              {t("nav.join")}
            </Link>
            <Link to="/consultation" className="rounded-full bg-gold px-4 py-2 text-xs font-bold text-navy">
              {t("nav.book")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
